/**
 * RadioField component
 */

// Library
import { ChangeEvent, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

// CSS Style for this module
import styles from './radioField.module.scss';

/**
 * Interface for RadioField props
 */
interface RadioFieldProps {
	/**
	 * Custom styles of the button
	 */
	customStyles?: string;
	/**
	 * Check the radio input
	 */
	isChecked?: boolean;
	/**
	 * The value of the radio input
	 */
	value: string;
	/**
	 * onChange function for keeping track field value
	 */
	onChange?: (value: string) => void;
	/**
	 * label text
	 */
	label?: string;
	/**
	 * Whether it lets interact with the radio input through the label or not. True by default
	 */
	isLabelClickable?: boolean;
	/**
	 * Helper text for additional description displayed on hover
	 */
	hoverText?: string;
	/**
	 * Boolean for determining whether this is a disabled field
	 */
	disabled?: boolean;
	/**
	 * Boolean for determining whether to display this field or not
	 */
	hidden?: boolean;
	/**
	 * Label content instead of label text
	 */
	children?: React.ReactNode;
	/** Element identifier selector for test */
	testId?: string;
}

/**
 * Radio field
 * @param {RadioFieldProps} Prop
 * @returns an styled radio input element
 */
export const RadioField = ({
	customStyles,
	isChecked,
	value,
	label,
	hoverText,
	onChange,
	isLabelClickable = true,
	disabled = false,
	hidden = false,
	children,
	testId,
}: RadioFieldProps) => {
	const [id] = useState(uuidv4());

	const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
		if (onChange) {
			onChange(event.target.value);
		}
	};

	return hidden ? (
		<div></div>
	) : (
		<div className={[styles.container, customStyles].join(' ')} title={hoverText}>
			<input
				type="radio"
				value={value}
				checked={isChecked}
				onChange={handleRadioChange}
				disabled={disabled}
				className={styles.field}
				id={id}
				data-testid={testId}
			/>
			{(children || label) && (
				<label
					className={[
						styles.label,
						styles[`label-disabled-${disabled}`],
						styles[`label-disabled-checked-${disabled}-${isChecked}`],
						styles[`label-clickable-${isLabelClickable}`],
					].join(' ')}
					htmlFor={isLabelClickable ? id : undefined}
				>
					{children ? children : label}
				</label>
			)}
		</div>
	);
};
